import {
  Select as SelectPrimitive,
  type SelectRootItemComponentProps,
  type SelectRootSectionComponentProps,
} from "@kobalte/core/select";
import { Check, ChevronsUpDown, Plus } from "@fleet/icons";
import { type Component, type JSX, Show, splitProps } from "solid-js";
import { cn } from "@cashsync/shared";
import { overlayPortalHost } from "./overlay-portal";

/** A single selectable option. */
interface SelectOption {
  value: string;
  /** Display text. `null`/`undefined` render as empty (matches native `<option>`). */
  label: string | null | undefined;
  disabled?: boolean;
}

/**
 * CTA rendered inside the dropdown when the Select has no options (AWS-style:
 * an empty combobox offers "Create X" / "Go to X" instead of a dead control).
 *
 * Pass `onSelect` to run an action (open a creation dialog) or `href` to render
 * a link (navigate to the screen that provides the options). Provide exactly one.
 * `can` (default `true`) gates the CTA on permission — when `false` the empty
 * dropdown falls back to a plain "no options" message and no creation affordance
 * is shown. Omit `emptyAction` entirely when the source is not creatable.
 */
export interface SelectEmptyAction {
  label: string;
  onSelect?: () => void;
  href?: string;
  /** Permission gate. When false, the CTA is hidden. Defaults to true.*/
  can?: boolean;
  /** Leading icon for the CTA. Defaults to a plus glyph.*/
  icon?: JSX.Element;
  /** Optional helper line shown above the CTA (e.g. "No accounts yet"). */
  description?: string;
}

/** A labelled group of options (renders like a native `<optgroup>`). */
interface SelectGroup {
  label: string;
  options: SelectOption[];
}

interface SelectProps {
  /** Flat list of options. Provide this OR `groups`, not both. */
  options?: SelectOption[];
  /** Grouped options, rendered under section headings. */
  groups?: SelectGroup[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Accessible label when no visible `<label>` is associated. */
  "aria-label"?: string;
  id?: string;
  /** Extra classes for the trigger (input-like button). */
  class?: string;
  /** Extra classes for the dropdown panel. */
  contentClass?: string;
  /**
   * Shown inside the dropdown when there are no options. Lets the user jump to
   * the place that creates options instead of facing a dead Select. See
   * {@link SelectEmptyAction}.
   */
  emptyAction?: SelectEmptyAction;
  /** Message shown in the empty dropdown when there is no (permitted) CTA.*/
  emptyLabel?: string;
}

const itemClass = cn(
  "relative flex cursor-pointer select-none items-center justify-between gap-2 rounded-md px-3 py-2 text-sm outline-none",
  "text-foreground transition-colors",
  "data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground",
  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
);

const SelectItem: Component<SelectRootItemComponentProps<SelectOption>> = (props) => (
  <SelectPrimitive.Item item={props.item} class={itemClass}>
    <SelectPrimitive.ItemLabel class="truncate">
      {props.item.rawValue.label}
    </SelectPrimitive.ItemLabel>
    <SelectPrimitive.ItemIndicator class="shrink-0">
      <Check class="h-4 w-4 text-accent" />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
);

const SelectSection: Component<SelectRootSectionComponentProps<SelectGroup>> = (props) => (
  <SelectPrimitive.Section
    class={cn(
      "px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground",
      props.section.rawValue.label ? "pb-1 pt-2" : "p-0",
    )}
  >
    {props.section.rawValue.label}
  </SelectPrimitive.Section>
);

function Panel(props: {
  class?: string;
  isEmpty?: boolean;
  emptyAction?: SelectEmptyAction;
  emptyLabel?: string;
}) {
  return (
    // mount: host tagged data-live-announcer so the modal Dialog's
    // ariaHideOutside does NOT aria-hide the listbox (CC-21) — see overlay-portal.ts.
    <SelectPrimitive.Portal mount={overlayPortalHost()}>
      <SelectPrimitive.Content
        class={cn(
          "z-[300] min-w-[--kb-popper-anchor-width] overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md outline-none",
          "ui-select-content motion-reduce:animate-none",
          props.class,
        )}
      >
        <Show
          when={!props.isEmpty}
          fallback={<SelectEmptyContent action={props.emptyAction} label={props.emptyLabel} />}
        >
          <SelectPrimitive.Listbox class="max-h-60 overflow-y-auto p-1" />
        </Show>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

/**
 * Content shown inside an empty Select dropdown. Renders the `emptyAction` CTA
 * (creation button or navigation link) when present and permitted, otherwise a
 * muted "no options" message. The CTA closes the dropdown on activation.
 *
 * The CTA is keyboard-reachable (it is a native button/anchor, auto-focusable
 * inside the portalled panel) and inherits the panel's opacity-only entrance, so
 * it respects `prefers-reduced-motion` via the global guard like the rest of the UI.
 */
export function SelectEmptyContent(props: { action?: SelectEmptyAction; label?: string }) {
  const ctaClass = cn(
    "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium outline-none",
    "text-foreground transition-colors cursor-pointer",
    "hover:bg-accent hover:text-accent-foreground",
    "focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
  );

  const ctaIcon = () => props.action?.icon ?? <Plus class="h-4 w-4 shrink-0 text-accent" />;

  return (
    <Show
      when={props.action && props.action.can !== false ? props.action : undefined}
      fallback={
        <div class="px-3 py-3 text-center text-sm text-muted-foreground">
          {/* Copy lives at the call site so this primitive stays i18n-clean; the
              neutral em-dash is the last-resort placeholder when no label is given. */}
          {props.label ?? "—"}
        </div>
      }
    >
      {(action) => (
        <div class="space-y-1 p-1">
          <Show when={action().description}>
            <p class="px-3 pt-1 text-xs text-muted-foreground">{action().description}</p>
          </Show>
          <Show
            when={action().href}
            fallback={
              <button
                type="button"
                class={ctaClass}
                onClick={() => {
                  action().onSelect?.();
                }}
              >
                {ctaIcon()}
                <span class="truncate">{action().label}</span>
              </button>
            }
          >
            <a href={action().href} class={ctaClass}>
              {ctaIcon()}
              <span class="truncate">{action().label}</span>
            </a>
          </Show>
        </div>
      )}
    </Show>
  );
}

function Trigger(props: { id?: string; "aria-label"?: string; class?: string }) {
  return (
    <SelectPrimitive.Trigger
      id={props.id}
      aria-label={props["aria-label"]}
      class={cn(
        "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm",
        "text-foreground",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        "transition-colors",
        props.class,
      )}
    >
      <SelectPrimitive.Value<SelectOption> class="block truncate text-left">
        {(state) => state.selectedOption()?.label}
      </SelectPrimitive.Value>
      <SelectPrimitive.Icon class="shrink-0 opacity-60">
        <ChevronsUpDown class="h-4 w-4" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/**
 * Premium Select built on `@kobalte/core` — keyboard, typeahead and ARIA come
 * for free. Theme-safe: only design tokens, no hardcoded colors.
 *
 * The Kobalte primitive is generic over the option object. We keep a flat
 * lookup of every option (the source of truth for the current value) and feed
 * Kobalte either the flat list or the group list depending on the props given.
 */
export function Select(props: SelectProps): JSX.Element {
  const [local] = splitProps(props, [
    "options",
    "groups",
    "value",
    "onChange",
    "placeholder",
    "disabled",
    "aria-label",
    "id",
    "class",
    "contentClass",
    "emptyAction",
    "emptyLabel",
  ]);

  const allOptions = (): SelectOption[] => [
    ...(local.options ?? []),
    ...(local.groups ?? []).flatMap((g) => g.options),
  ];

  const isEmpty = () => allOptions().length === 0;

  const selectedOption = () => allOptions().find((o) => o.value === local.value) ?? null;

  const handleChange = (option: SelectOption | null) => {
    // `null` happens when the control is cleared; coalesce to empty string so
    // callers keep the "no selection" convention used by the old <select>.
    local.onChange(option?.value ?? "");
  };

  const placeholder = () => <span class="text-muted-foreground">{local.placeholder ?? ""}</span>;

  // Always feed Kobalte a uniform group list (single code path → reactive,
  // no early return). Leading ungrouped `options` (e.g. an "All categories"
  // reset row) become a label-less section above the labelled groups; the
  // section header renders empty/zero-height for the "" label.
  const groups = (): SelectGroup[] => [
    ...(local.options && local.options.length > 0 ? [{ label: "", options: local.options }] : []),
    ...(local.groups ?? []),
  ];

  return (
    <SelectPrimitive<SelectOption, SelectGroup>
      options={groups()}
      optionValue="value"
      optionTextValue="label"
      optionDisabled="disabled"
      optionGroupChildren="options"
      value={selectedOption()}
      // Single-select required: re-clicking the ALREADY selected option must NOT
      // toggle it to empty. By default Kobalte emits `null` when re-clicking the
      // current selection (deselect) → handleChange coerces to "" and cleared the
      // field before submit (e.g. re-choosing the same account/category/currency).
      // With `disallowEmptySelection` the value can be CHANGED to another option but
      // not emptied by interaction. (CC: Select re-click deselects.)
      disallowEmptySelection
      onChange={handleChange}
      disabled={local.disabled}
      placeholder={placeholder()}
      itemComponent={SelectItem}
      sectionComponent={SelectSection}
    >
      <Trigger id={local.id} aria-label={local["aria-label"]} class={local.class} />
      <Panel
        class={local.contentClass}
        isEmpty={isEmpty()}
        emptyAction={local.emptyAction}
        emptyLabel={local.emptyLabel}
      />
    </SelectPrimitive>
  );
}
